import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight, Award } from "lucide-react";
import { fetchPaymentStatus, money } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("polling"); // polling | paid | failed
  const [data, setData] = useState(null);
  const attempts = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const res = await fetchPaymentStatus(sessionId);
        setData(res);
        if (res.payment_status === "paid") {
          setState("paid");
          if (!cleared.current) {
            clear();
            cleared.current = true;
          }
          return;
        }
        if (["failed", "expired"].includes(res.payment_status)) {
          setState("failed");
          return;
        }
      } catch (e) {
        /* keep polling */
      }
      attempts.current += 1;
      if (attempts.current >= 8) {
        setState("timeout");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clear]);

  return (
    <div className="min-h-screen flex items-center justify-center px-5 pt-28">
      <div className="max-w-lg w-full text-center" data-testid="payment-success-page">
        {state === "polling" && (
          <>
            <Loader2 size={56} className="text-accent animate-spin mx-auto mb-6" />
            <h1 className="font-display font-black text-4xl tracking-tight">CONFIRMING PAYMENT…</h1>
            <p className="text-zinc-400 mt-3 font-mono-tech text-xs uppercase tracking-widest">
              Please don't close this window
            </p>
          </>
        )}

        {state === "paid" && (
          <>
            <CheckCircle2 size={64} className="text-emerald-400 mx-auto mb-6" />
            <span className="font-mono-tech text-xs tracking-[0.3em] uppercase text-accent">
              / Order confirmed
            </span>
            <h1 className="font-display font-black text-5xl tracking-tighter mt-3 leading-none">
              YOU'RE SET.
            </h1>
            <p className="text-zinc-300 mt-5 leading-relaxed">
              Thanks for your order. Your research supplies are being prepared and will be dispatched
              express, cold-packed, Australia-wide.
              {data?.amount ? ` Total paid: ${money(data.amount)}.` : ""}
            </p>
            {data?.points_earned > 0 && (
              <div className="inline-flex items-center gap-2 border border-amber-400/40 bg-amber-400/5 px-4 py-2.5 mt-5 clip-corner" data-testid="points-earned">
                <Award size={16} className="text-amber-400" />
                <span className="font-mono-tech text-[11px] uppercase tracking-widest text-amber-400">
                  You earned {data.points_earned} reward points
                </span>
              </div>
            )}
            <p className="font-mono-tech text-[11px] text-zinc-600 mt-4 break-all">
              Ref: {sessionId}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
              <Link
                to="/shop"
                className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-display font-bold uppercase tracking-wide px-7 py-4 transition-colors clip-corner"
                data-testid="continue-shopping"
              >
                Continue Shopping <ArrowRight size={18} />
              </Link>
              <Link
                to="/rewards"
                className="inline-flex items-center gap-2 border border-white/25 hover:border-white text-white font-display font-bold uppercase tracking-wide px-7 py-4 transition-colors"
              >
                View Rewards
              </Link>
            </div>
          </>
        )}

        {(state === "failed" || state === "timeout") && (
          <>
            <XCircle size={64} className="text-accent mx-auto mb-6" />
            <h1 className="font-display font-black text-4xl tracking-tighter">
              {state === "timeout" ? "STILL PROCESSING" : "PAYMENT NOT COMPLETED"}
            </h1>
            <p className="text-zinc-400 mt-4 leading-relaxed">
              {state === "timeout"
                ? "Your payment is taking longer than expected. If you were charged, your order will be confirmed shortly."
                : "We couldn't confirm this payment. No charge has been made — please try again."}
            </p>
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 border border-white/25 hover:border-white text-white font-display font-bold uppercase tracking-wide px-7 py-4 mt-8 transition-colors"
            >
              Back to Shop
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
